import type { FeatureFlag } from '@repo/feature-flags'
import type { UserPermission } from '@repo/permissions'
import {
  ArmchairIcon,
  CalculatorIcon,
  ClipboardCheckIcon,
  ClockArrowUpIcon,
  ClockIcon,
  FileCheck2Icon,
  FilePenIcon,
  FileTextIcon,
  FileStackIcon,
  FileXCornerIcon,
  FolderClockIcon,
  FolderDownIcon,
  FolderUpIcon,
  GaugeIcon,
  InboxIcon,
  KeyRoundIcon,
  LayersIcon,
  LineChartIcon,
  ListChecksIcon,
  MapPinIcon,
  PackageXIcon,
  ScaleIcon,
  SendIcon,
  SettingsIcon,
  ShieldAlertIcon,
  TableIcon,
  UserRoundIcon,
  UserRoundPlusIcon,
  UsersIcon,
  UserStarIcon,
  type LucideIcon,
} from 'lucide-react'

export type CreditRouteEntry = {
  title: string
  crumb?: string
  icon?: LucideIcon
  permissions?: UserPermission[]
  keywords?: string[]
  paramName?: string
  /** Hidden from the sidebar and command menu, and its route redirects, while the flag is off. */
  featureFlag?: FeatureFlag
}

export function toStaticData(entry: CreditRouteEntry) {
  return {
    title: entry.title,
    crumb: entry.crumb ?? entry.title,
    icon: entry.icon,
    permissions: entry.permissions,
  }
}

export const CREDIT_ROUTES = {
  // ── Root ──
  '/credit': {
    title: 'route.credit',
  },

  // ── Welcome ──
  '/credit/welcome': {
    title: 'route.welcome',
    crumb: 'breadcrumbs.welcomeCredit',
    icon: ArmchairIcon,
  },

  // ── Customers ──
  '/credit/customers': {
    title: 'route.credit.customers',
    icon: UsersIcon,
    permissions: ['credit_customers_view'],
    keywords: ['customers', 'kna1', 'master data', 'sap', 'credit', 'universe'],
  },

  // ── Credit Index Scores ──
  '/credit/credit-index-scores': {
    title: 'route.credit.indexScores',
    icon: GaugeIcon,
    permissions: ['credit_index_scores_view'],
    keywords: ['credit', 'index', 'score', 'equifax', 'erp'],
  },

  // ── Google Financial Statements ──
  '/credit/google-financial-statements': {
    title: 'route.credit.googleFinancialStatements',
    icon: LineChartIcon,
    permissions: ['google_financial_statements_view'],
    keywords: ['google', 'financial', 'statements', 'profit', 'ratio', 'erp'],
    paramName: 'sapErp',
  },

  // ── Operations (SAP imports) ──
  '/credit/operations': {
    title: 'route.credit.operations',
    icon: SettingsIcon,
    permissions: ['credit_operations_view'],
    keywords: ['operations', 'sap', 'import', 'customer', 'sales', 'partner', 'master data'],
  },

  // ── Operations → Multiplier (Assumptions) ──
  '/credit/operations/multiplier': {
    title: 'route.credit.multiplier',
    icon: CalculatorIcon,
    permissions: ['credit_multiplier_view'],
    keywords: ['multiplier', 'assumptions', 'risk class', 'payment term', 'pad'],
  },

  // ── Operations → Order Cutoffs ──
  '/credit/operations/order-cutoffs': {
    title: 'route.credit.orderCutoffs',
    icon: ClockIcon,
    permissions: ['credit_order_cutoff_view'],
    keywords: ['cutoff', 'order', 'blocked', 'shipping', 'province', 'deadline', 'ship by'],
  },

  // ── Operations → Province AOV per Carton (Assumptions) ──
  '/credit/operations/province-aov-per-carton': {
    title: 'route.credit.provinceAovPerCarton',
    icon: MapPinIcon,
    permissions: ['credit_province_aov_view'],
    keywords: ['province', 'aov', 'carton', 'assumptions', 'new account', 'credit limit'],
  },

  // ── Operations → Very Low Risk Customers (Assumptions) ──
  '/credit/operations/very-low-risk-customers': {
    title: 'route.credit.veryLowRiskCustomers',
    icon: UserStarIcon,
    permissions: ['credit_very_low_risk_customers_view'],
    keywords: ['very low risk', 'customers', 'assumptions', 'risk class', 'erp'],
  },

  // ── Review Events ──
  '/credit/credit-limit-reviews/review-events': {
    title: 'route.credit.reviewEvents',
    icon: ClipboardCheckIcon,
    permissions: ['credit_review_events_view'],
    keywords: [
      // The section was called "Periodic Reviews" until CT-9; people who learned that name have to
      // keep finding it by typing it, and this is the only place they now can.
      'periodic',
      'trigger',
      'triggers',
      'review',
      'events',
      'resolved',
      'erp',
      'credit limit',
      'failed payment',
      'deposit',
    ],
  },

  // ── Pending SAP confirmations (CT-21) ──
  '/credit/credit-limit-reviews/pending-confirmations': {
    title: 'route.credit.pendingConfirmations',
    icon: FileCheck2Icon,
    permissions: ['credit_limit_reviews_view'],
    keywords: ['sap', 'pending', 'confirmation', 'dispatch', 'segment', 'knkk', 'clr'],
  },
  '/credit/credit-limit-reviews/mass/pending-confirmations': {
    title: 'route.credit.massPendingConfirmations',
    icon: FileCheck2Icon,
    permissions: ['credit_limit_reviews_view'],
    keywords: ['sap', 'pending', 'confirmation', 'dispatch', 'mass', 'run'],
  },
  '/credit/credit-limit-reviews/mass/$runId/pending-confirmations': {
    title: 'route.credit.massRunPendingConfirmations',
    icon: FileCheck2Icon,
    permissions: ['credit_limit_reviews_view'],
  },

  // ── Inbox Everything pending the signed-in user's decision ──
  '/credit/inbox': {
    title: 'route.credit.inbox',
    icon: InboxIcon,
    permissions: ['credit_limit_reviews_view'],
    keywords: ['inbox', 'tasks', 'review', 'approve', 'clr', 'mass'],
  },

  // ── Credit Limit Reviews ──
  '/credit/credit-limit-reviews': {
    title: 'route.credit.creditLimitReviews',
    icon: ListChecksIcon,
    permissions: ['credit_limit_reviews_view'],
    keywords: ['clr', 'credit limit', 'review', 'approval', 'doa', 'preparer', 'approver'],
  },
  '/credit/credit-limit-reviews/$reviewId': {
    title: 'route.credit.creditLimitReview',
    icon: FileTextIcon,
    permissions: ['credit_limit_reviews_view'],
    paramName: 'reviewId',
  },
  // ── Blocked Orders (cockpit) ──
  // Release and reject are gated per order by DOA, not by RBAC — in the inbox and on a group's page —
  // so this carries the view permission only.
  '/credit/blocked-orders': {
    title: 'route.credit.blockedOrders',
    icon: PackageXIcon,
    permissions: ['credit_blocked_orders_view'],
    keywords: ['blocked', 'order', 'release', 'reject', 'ceb', 'cutoff', 'sap', 'dcd', 'cockpit'],
  },
  // One cockpit group — a payer's orders awaiting review or approval, or one decision's orders —
  // reached from the cockpit's grouped row. Pending groups are decided here, by DOA.
  '/credit/blocked-orders/groups/$groupKey': {
    title: 'route.credit.blockedOrderGroup',
    icon: PackageXIcon,
    permissions: ['credit_blocked_orders_view'],
    paramName: 'groupKey',
  },

  // ── Mass Reviews ──
  '/credit/credit-limit-reviews/mass': {
    title: 'route.credit.massReviews',
    icon: LayersIcon,
    permissions: ['credit_limit_reviews_view'],
    keywords: ['mass', 'clr', 'batch', 'run', 'bulk', 'review', 'credit limit'],
  },
  '/credit/credit-limit-reviews/mass/new': {
    title: 'route.credit.massReviewNew',
    icon: FilePenIcon,
    permissions: ['credit_limit_reviews_view'],
  },
  '/credit/credit-limit-reviews/mass/$runId': {
    title: 'route.credit.massReviewRun',
    icon: LayersIcon,
    permissions: ['credit_limit_reviews_view'],
    paramName: 'runId',
  },
  '/credit/credit-limit-reviews/mass/$runId/lines': {
    title: 'route.credit.massReviewLines',
    icon: TableIcon,
    permissions: ['credit_limit_reviews_view'],
  },
  '/credit/credit-limit-reviews/mass/$runId/submit': {
    title: 'route.credit.massReviewSubmit',
    icon: SendIcon,
    permissions: ['credit_limit_reviews_view'],
  },

  // ── Initiatives ──
  '/credit/initiatives': {
    title: 'route.credit.initiatives',
    icon: ClockArrowUpIcon,
    permissions: ['credit_initiatives_view'],
    featureFlag: 'initiatives',
    keywords: ['initiative', 'initiatives', 'uplift', 'marketing', 'payer', 'injection'],
  },
  '/credit/initiatives/$id': {
    title: 'route.credit.initiativeDetail',
    icon: ClockArrowUpIcon,
    permissions: ['credit_initiatives_view'],
    paramName: 'id',
    featureFlag: 'initiatives',
  },

  // ── Uploads ──
  '/credit/my-uploads': {
    title: 'route.uploads',
    icon: FolderUpIcon,
    permissions: ['credit_upload_myUpload_view'],
  },

  // ── My Exports (background exports, e.g. the SAP tables in their import layout) ──
  '/credit/my-exports': {
    title: 'route.myExports',
    icon: FolderDownIcon,
    keywords: ['my', 'exports', 'downloads', 'csv', 'sap'],
    permissions: ['credit_sap_imports_create'],
  },

  // ── Delegation of Authority (nested under Users; reached via Users → Configuration) ──
  '/credit/admin/users/delegation-of-authority': {
    title: 'route.credit.delegationOfAuthority',
    crumb: '',
    icon: ScaleIcon,
    permissions: ['credit_delegation_of_authority_view'],
    keywords: ['delegation', 'authority', 'doa', 'approval', 'reviewer', 'approver', 'clr'],
  },
  '/credit/admin/users/delegation-of-authority/single-no-deviation': {
    title: 'route.credit.doaSingleNoDeviation',
    icon: FileCheck2Icon,
    permissions: ['credit_delegation_of_authority_view'],
  },
  '/credit/admin/users/delegation-of-authority/single-with-deviation': {
    title: 'route.credit.doaSingleWithDeviation',
    icon: FileXCornerIcon,
    permissions: ['credit_delegation_of_authority_view'],
  },
  '/credit/admin/users/delegation-of-authority/mass': {
    title: 'route.credit.doaMass',
    icon: FileStackIcon,
    permissions: ['credit_delegation_of_authority_view'],
  },
  '/credit/admin/users/delegation-of-authority/initiative': {
    title: 'route.credit.doaInitiative',
    icon: ClockArrowUpIcon,
    permissions: ['credit_delegation_of_authority_view'],
    featureFlag: 'initiatives',
  },
  '/credit/admin/users/delegation-of-authority/blocked-orders': {
    title: 'route.credit.doaBlockedOrders',
    icon: PackageXIcon,
    permissions: ['credit_delegation_of_authority_view'],
  },
  // A sub-config of blocked orders, not a sixth DOA type: the payer ERPs whose blocked orders
  // bypass the CEB band. Reached from the blocked-orders page header.
  '/credit/admin/users/delegation-of-authority/blocked-orders/exception-payers': {
    title: 'route.credit.doaExceptionPayers',
    icon: ShieldAlertIcon,
    permissions: ['credit_delegation_of_authority_view'],
    keywords: ['exception', 'payer', 'bypass', 'couche-tard', 'blocked orders'],
  },

  // ── Admin ──
  '/credit/admin': {
    title: 'Admin',
    icon: UserRoundIcon,
  },
  '/credit/admin/users': {
    title: 'route.admin.users',
    icon: UsersIcon,
    permissions: ['credit_users_view'],
  },
  '/credit/admin/invitations': {
    title: 'Invitations',
    icon: UserRoundPlusIcon,
    permissions: ['credit_invites_view'],
  },
  '/credit/admin/roles': {
    title: 'route.admin.roles',
    icon: KeyRoundIcon,
    permissions: ['credit_roles_view'],
  },
  '/credit/admin/audit-trails': {
    title: 'route.admin.auditTrails',
    icon: FolderClockIcon,
    permissions: ['credit_audit_trails_view'],
  },
} as const satisfies Record<string, CreditRouteEntry>

export type CreditRoutePath = keyof typeof CREDIT_ROUTES
